import { useEffect, useState } from "react";
import { get } from "../../../redux/ApiHelper";
import { Column } from "../../../interfaces/table.interface";
import TableComponent from "../../../components/table/TableComponent";
import { formatDateTime } from "../../../utils/date&time.util";
import { useDispatch } from "react-redux";
import { startLoading, stopLoading } from "../../../redux/slices/WindowSlice";
import { errorToast } from "../../../utils/toast.util";
import { OtherCoinTransferInterface } from "../../../interfaces/otherCoinTransfer.interface";

const personName = (person: unknown) => {
  if (person && typeof person === "object" && "name" in person) {
    return String((person as { name?: string }).name || "N/A");
  }
  return "N/A";
};

const resolveDirection = (row: OtherCoinTransferInterface) => {
  if (row.isParentReceived || row.isTeacherReceived) return "Received";
  if (row.isDeductedFromParent || row.isDeductedFromTeacher) return "Deducted";
  return "N/A";
};

const OtherCoinTransferTab = () => {
  const dispatch = useDispatch();
  const [transfers, setTransfers] = useState<OtherCoinTransferInterface[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [totalResults, setTotalResults] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchQuery.trim();
      setDebouncedSearch((prev) => {
        if (prev !== next) {
          setPage(0);
        }
        return next;
      });
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    dispatch(startLoading());
    const queryParams = new URLSearchParams({
      page: String(page + 1),
      limit: String(rowsPerPage),
    });
    if (debouncedSearch) {
      queryParams.set("search", debouncedSearch);
    }
    get(
      `/admin/other-coin-transfer/other-coin-transfers?${queryParams.toString()}`
    )
      .then((res) => {
        const list = Array.isArray(res?.otherCoinTransfers)
          ? res.otherCoinTransfers
          : [];
        setTransfers(list);
        setTotalResults(
          typeof res?.totalResults === "number" ? res.totalResults : list.length
        );
      })
      .catch(() => {
        errorToast("Failed to load other coin transfers");
      })
      .finally(() => {
        dispatch(stopLoading());
      });
  }, [dispatch, page, rowsPerPage, debouncedSearch]);

  const columns: Column[] = [
    {
      select: "_id",
      header: "ID",
      align: "left",
      render: (value: string) => (
        <span className="text-xs font-mono break-all">{value}</span>
      ),
    },
    {
      header: "Reason",
      render: (_: unknown, row: OtherCoinTransferInterface) =>
        row.transactionFor || "N/A",
    },
    {
      header: "Ledger role",
      render: (_: unknown, row: OtherCoinTransferInterface) =>
        row.role || "N/A",
    },
    {
      header: "Coins",
      render: (_: unknown, row: OtherCoinTransferInterface) => row.coins ?? 0,
    },
    {
      header: "Parent",
      render: (_: unknown, row: OtherCoinTransferInterface) =>
        personName(row.parentId),
    },
    {
      header: "Teacher",
      render: (_: unknown, row: OtherCoinTransferInterface) =>
        personName(row.teacherId),
    },
    {
      header: "Direction",
      render: (_: unknown, row: OtherCoinTransferInterface) => {
        const direction = resolveDirection(row);
        const tone =
          direction === "Received"
            ? "text-green-500"
            : direction === "Deducted"
              ? "text-red-400"
              : "text-gray-400";
        return <span className={`font-medium ${tone}`}>{direction}</span>;
      },
    },
    {
      header: "Tuition request",
      render: (_: unknown, row: OtherCoinTransferInterface) => (
        <span className="text-xs font-mono break-all">
          {row.tuitionRequestId || "—"}
        </span>
      ),
    },
    {
      header: "Period",
      render: (_: unknown, row: OtherCoinTransferInterface) => (
        <span className="text-xs font-mono break-all">
          {row.tuitionPeriodId || "—"}
        </span>
      ),
    },
    {
      header: "Date",
      render: (_: unknown, row: OtherCoinTransferInterface) =>
        formatDateTime(row?.createdAt),
    },
  ];

  return (
    <div>
      <div className="search-container mb-4">
        <input
          type="text"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-700"
          placeholder="Search by parent/teacher name, phone, email, reason, role, or ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      <TableComponent
        columns={columns}
        data={transfers}
        serverSidePagination={{
          page,
          rowsPerPage,
          count: totalResults,
          rowsPerPageOptions: [15, 25, 40, 100],
          onPageChange: (newPage) => setPage(newPage),
          onRowsPerPageChange: (next) => {
            setRowsPerPage(next);
            setPage(0);
          },
        }}
      />
    </div>
  );
};

export default OtherCoinTransferTab;
